import posthog from 'posthog-js';

const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;

export function initPostHog(): void {
  if (!key) return;
  posthog.init(key, {
    api_host: 'https://eu.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-mask]',
    },
    loaded: (ph) => {
      // Capture initial UTM params
      const params = new URLSearchParams(window.location.search);
      const utmSource = params.get('utm_source');
      if (utmSource) {
        ph.register({
          utm_source:   utmSource,
          utm_medium:   params.get('utm_medium')   ?? undefined,
          utm_campaign: params.get('utm_campaign') ?? undefined,
          utm_content:  params.get('utm_content')  ?? undefined,
          utm_term:     params.get('utm_term')      ?? undefined,
        });
      }
    },
  });
}

export function phIdentify(userId: string, props?: Record<string, unknown>): void {
  if (!key) return;
  posthog.identify(userId, props);
}

export function phReset(): void {
  if (!key) return;
  posthog.reset();
}

// ─── Event tracking functions ─────────────────────────────────────────────────

export function phTrack(event: string, props?: Record<string, unknown>): void {
  if (!key) return;
  posthog.capture(event, props);
}

// Specific typed event helpers matching the document spec
export const ph = {
  signupCompleted: (method: 'email' | 'google') =>
    phTrack('signup_completed', { method }),

  natalChartSaved: () =>
    phTrack('natal_chart_saved'),

  serviceViewed: (service: string) =>
    phTrack('service_viewed', { service }),

  serviceGenerated: (service: string, durationMs: number) =>
    phTrack('service_generated', { service, duration_ms: durationMs }),

  paywallHit: (service: string, fromScreen: string) =>
    phTrack('paywall_hit', { service, from_screen: fromScreen }),

  upgradeClicked: (fromScreen: string) =>
    phTrack('upgrade_clicked', { from_screen: fromScreen }),

  subscriptionStarted: (plan: 'monthly' | 'annual', method: string) =>
    phTrack('subscription_started', { plan, method }),

  subscriptionCancelled: (plan: string, daysActive: number, reason?: string) =>
    phTrack('subscription_cancelled', { plan, days_active: daysActive, reason }),

  forecastScrollDepth: (period: string, depthPercent: number) =>
    phTrack('forecast_scroll_depth', { period, depth_percent: depthPercent }),

  astroCoachWeekOpened: (week: number) =>
    phTrack('astro_coach_week_opened', { week }),

  referralShared: (channel: string) =>
    phTrack('referral_shared', { channel }),

  llmCall: (service: string, model: string, durationMs: number, success: boolean, cachedResult: boolean) =>
    phTrack('llm_call', { service, model, duration_ms: durationMs, success, cached: cachedResult }),
};
